import type { Attachment } from './attachment';
import type { CustomerApprovalMethod } from './estimate';

// ─── Company / Tenant ─────────────────────────────────────────────────────────

export interface BusinessHours {
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  open: boolean;
  openTime: string; // HH:mm  e.g. "08:00"
  closeTime: string; // HH:mm  e.g. "17:00"
}

export interface BusinessBlackoutDate {
  date: string;
  label?: string;
  annual: boolean;
  rule?: BusinessBlackoutDateRule;
}

export type BusinessBlackoutDateRule =
  | {
      kind: 'nthWeekdayOfMonth';
      month: number;
      weekday: number;
      ordinal: number;
      offsetDays?: number;
    }
  | {
      kind: 'lastWeekdayOfMonth';
      month: number;
      weekday: number;
      offsetDays?: number;
    };

export type BusinessBlackoutDateInput = string | BusinessBlackoutDate;

export interface CompanyAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

// ─── Estimate Settings ────────────────────────────────────────────────────────

export interface EstimatePaymentScheduleSettings {
  /** Allow attaching payment schedules to estimates (spec §8). */
  allowPaymentSchedules: boolean;
  /** If estimate total >= this amount, the user must attach a schedule. 0 = off. */
  requireScheduleAboveAmount: number;
  /** Default preset to seed the builder with. null = none. */
  defaultPresetId: string | null;
  /** Default value for the auto-charge toggle on each new schedule rule. */
  autoChargeDefault: boolean;
  /** Days after job completion that customer ACH/card consent stays valid. 1–365. */
  consentExpirationDays: number;
  /** Show the payment plan preview block on the public accept page. */
  showPaymentPlanPreview: boolean;
}

/**
 * Customer-facing presentation mode (ServiceTitan-style online-estimate view
 * preference). ITEMIZED shows the per-line pricing table; SINGLE_SCOPE folds the
 * work into one scope price with a plain "what's included" checklist.
 */
export type EstimatePresentation = 'ITEMIZED' | 'SINGLE_SCOPE';

export interface EstimateSettings {
  /** How the customer estimate is presented — itemized table vs single scope.
   * Optional/absent reads as ITEMIZED so existing tenants are unchanged. */
  presentation?: EstimatePresentation;
  signatureRequired: boolean;
  /** Approval method pre-selected on new estimates. */
  defaultApprovalMethod: CustomerApprovalMethod;
  sendMeCopy: boolean;
  autoConvertToJob: boolean;
  /** Defaults to enabled; unscoped estimates still search the whole catalog. */
  filterPricebookByBusinessUnit?: boolean;
  estimateNameTemplate: string;
  /** The note pre-filled on every new estimate. The estimate EMAIL is a design (Designs → Emails). */
  defaultMessage: string;
  smsTemplate: string;
  expirationDays: number;
  depositType: 'percentage' | 'fixed';
  depositAmount: number;
  paymentSchedule: EstimatePaymentScheduleSettings;
}

export const ESTIMATE_APPROVAL_METHOD_LABELS: Record<CustomerApprovalMethod, string> = {
  SIGNATURE: 'Signature',
  ONLINE: 'Online',
  VERBAL: 'Verbal',
  EMAIL: 'Email',
};

export const DEFAULT_PAYMENT_SCHEDULE_SETTINGS: EstimatePaymentScheduleSettings = {
  allowPaymentSchedules: true,
  requireScheduleAboveAmount: 0,
  defaultPresetId: null,
  autoChargeDefault: false,
  consentExpirationDays: 60,
  showPaymentPlanPreview: true,
};

export interface CompanySettings {
  id: string;
  /** Also used as the From display name on customer-facing email. */
  name: string;
  logoUrl?: string;
  /** NOT-236 — inline data URI of the logo for embedding in browser-rendered
   *  PDFs (presigned `logoUrl` can't be used there: react-pdf fetches it
   *  cross-origin and S3 CORS blocks it, blanking the logo). */
  logoDataUri?: string;
  address?: CompanyAddress;
  timezone: string;
  /** Tenant-wide selling currency. Every price derives from it. */
  currency: 'USD' | 'CAD';
  businessHours: BusinessHours[];
  phone?: string;
  /** Public company mailbox and customer-email Reply-To address. */
  email?: string;
  website?: string;
  /** NOT-7 — government-issued tax ID (BN/HST/GST/EIN/VAT/etc.). */
  taxRegistrationNumber?: string;
  /** Marketing line under the company name, e.g. "Heating · Cooling · Plumbing". */
  tagline?: string;
  /** Contractor/trade license number(s), free-form (may list several). */
  contractorLicense?: string;
  regulatoryNotice?: string;
  jobNameTemplate?: string;
  jobNameEditable?: boolean;
}

export interface UpdateCompanyDto {
  name?: string;
  logoKey?: string;
  address?: CompanyAddress;
  timezone?: string;
  currency?: 'USD' | 'CAD';
  businessHours?: BusinessHours[];
  phone?: string;
  email?: string;
  website?: string;
  taxRegistrationNumber?: string;
  tagline?: string;
  contractorLicense?: string;
  regulatoryNotice?: string;
}

// ─── Business Settings (scheduler) ───────────────────────────────────────────

/**
 * Per-tenant scheduling defaults served by `GET /settings` on operations.
 * Distinct from CompanySettings (which is contact/profile info); these are the
 * knobs the scheduling engine consults when computing availability, slots, and
 * travel-time-aware conflicts.
 */
export interface BusinessSettings {
  organizationId: string;
  timezone: string;
  businessHoursStart: string; // HH:mm
  businessHoursEnd: string; // HH:mm
  slotDurationMinutes: number;
  leadTimeHours: number;
  maxAdvanceBookingDays: number;
  blackoutDates: BusinessBlackoutDate[]; // company calendar dates
  /** Per-weekday business hours — the canonical scheduling source (Option A). */
  businessHours: BusinessHours[];
  emergencyEnabled: boolean;
  /** §8.6 — live geofence auto-arrival opt-in (default off → shadow). */
  geofenceAutoArrival: boolean;
  /** §8.6 — also auto-start the job + push the tech on geofence arrival. */
  geofenceAutoStart: boolean;
  allowOverlappingSchedules?: boolean;
  /** Travel-time-aware scheduling (Round 4). */
  bufferPrepMinutes: number;
  bufferCleanupMinutes: number;
  travelAvgSpeedKmh: number;
  /** Uncommitted slots inside this ± window prefer the least-added-drive route. */
  maxAutoTimeShiftMinutes: number;
  /** §9 live tracking — how often the tech GPS emitter posts a ping (seconds). */
  gpsPingIntervalSeconds: number;
  /** §9 — drift (minutes) past which a live-ETA update re-notifies the customer. */
  liveEtaDriftThresholdMinutes: number;
  /** NOT-322 — per-tenant calendar prefs. 0–6 (0=Sunday … 6=Saturday). */
  weekStartDay: number;
  workWeekStartDay: number;
  defaultCalendarView: 'week' | 'workweek' | 'month' | 'list' | 'board';
  /** Scheduling v2 §12 — tenant-local HH:mm when tomorrow's arrival windows are committed + sent. */
  eveningWindowSendTime: string;
  /** Scheduling v2 §8 — committed DAY_FLEXIBLE windows may shift at most ± this many minutes. */
  committedWindowToleranceMin: number;
  /** Scheduling v2 §10 — route-day efficiency below this fraction (0.50–1.00) raises an alert. */
  efficiencyAlertThreshold: number;
  // Scheduling v2 §17 rebalance knobs — INERT until the rebalance solver
  // lands; persisted + exposed now so tenants can configure ahead.
  stabilityWeightDay: StabilityWeight;
  stabilityWeightTech: StabilityWeight;
  minMoveIntervalMonths: number;
  maxMovesPerRebalancePct: number;
  changeNoticeLeadDays: number;
  weatherFlexDays: WeatherFlexDay[];
  weatherFlexSpillover: WeatherFlexSpillover;
}

/** Scheduling v2 §17 — rebalance stability weights. */
export type StabilityWeight = 'LOW' | 'MEDIUM' | 'HIGH';
export type WeatherFlexSpillover = 'NEXT_FLEX_DAY' | 'EXTEND_WINDOW';

export interface WeatherFlexDay {
  weekday: number;
  maxVisitsPerTech: number | null;
}

export interface UpdateBusinessSettingsDto {
  timezone?: string;
  businessHoursStart?: string;
  businessHoursEnd?: string;
  slotDurationMinutes?: number;
  leadTimeHours?: number;
  maxAdvanceBookingDays?: number;
  blackoutDates?: BusinessBlackoutDateInput[];
  businessHours?: BusinessHours[];
  emergencyEnabled?: boolean;
  geofenceAutoArrival?: boolean;
  geofenceAutoStart?: boolean;
  allowOverlappingSchedules?: boolean;
  bufferPrepMinutes?: number;
  bufferCleanupMinutes?: number;
  travelAvgSpeedKmh?: number;
  maxAutoTimeShiftMinutes?: number;
  weekStartDay?: number;
  workWeekStartDay?: number;
  defaultCalendarView?: 'week' | 'workweek' | 'month' | 'list' | 'board';
  // Scheduling v2 — live routing knobs.
  eveningWindowSendTime?: string;
  committedWindowToleranceMin?: number;
  efficiencyAlertThreshold?: number;
  // Scheduling v2 §17 rebalance knobs — the API accepts them (persisted for the
  // upcoming solver) but the settings form renders them disabled and omits them.
  stabilityWeightDay?: StabilityWeight;
  stabilityWeightTech?: StabilityWeight;
  minMoveIntervalMonths?: number;
  maxMovesPerRebalancePct?: number;
  changeNoticeLeadDays?: number;
  weatherFlexDays?: WeatherFlexDay[];
  weatherFlexSpillover?: WeatherFlexSpillover;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export type UserStatus = 'active' | 'inactive' | 'suspended' | 'revoked';

export interface UserRoleSummary {
  id: string;
  name: string;
}

export interface TeamUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  status: string;
  membershipId: string;
  membershipStatus: string;
  roles: UserRoleSummary[];
  createdAt: string;
  /**
   * Only populated for users whose `status === 'invited'`. `'pending'` if
   * the latest invitation token is still valid and unconsumed,
   * `'expired'` if it timed out without being used. `null` otherwise —
   * the table cell falls back to the regular status pill.
   */
  invitationStatus?: 'pending' | 'expired' | null;
  /**
   * ISO timestamp of the most recent invitation token mint — i.e. when
   * the user was last emailed an invitation. Drives the "Sent
   * <relative-time>" tooltip on the invitation pill.
   */
  invitedAt?: string | null;
  /** NOT-294: id of the roster technician linked to this user, or null. */
  technicianId?: string | null;
}

export interface UserDetail extends TeamUser {
  resolvedPermissions: string[];
  updatedAt: string;
  roles: RoleDetail[];
  /** HTML email signature shown in the inbox composer's tiptap editor. */
  emailSignatureHtml: string | null;
  /** Plain-text fallback for `emailSignatureHtml`. */
  emailSignatureText: string | null;
  scheduleDisplayTimezoneMode: 'TENANT' | 'BROWSER' | 'SERVICE';
}

export interface UpdateScheduleDisplayTimezoneDto {
  mode: 'TENANT' | 'BROWSER' | 'SERVICE';
}

/** Self-edit payload for `PATCH /users/me/email-signature`. */
export interface UpdateEmailSignatureDto {
  emailSignatureHtml: string | null;
  emailSignatureText: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export type UserListResponse = PaginatedResponse<TeamUser>;

/**
 * Lean user shape returned by `GET /leads/assignable-users` — the subset the
 * assignee pickers need (name + status), scope-filtered server-side to exactly
 * the users the lead-assignment write path will accept.
 */
export type AssignableUser = Pick<
  TeamUser,
  'id' | 'email' | 'firstName' | 'lastName' | 'displayName' | 'status'
>;

export interface AssignableUsersResponse {
  data: AssignableUser[];
}

/**
 * NOT-509: lean user shape returned by `GET /users/sales-attribution-candidates`
 * — active members whose role grants `estimates:create`, i.e. the valid owners
 * for the Estimate "Salesperson" and Job "Sales rep" pickers. No `status`: the
 * endpoint returns only active users.
 */
export type SalesAttributionUser = Pick<
  TeamUser,
  'id' | 'email' | 'firstName' | 'lastName' | 'displayName'
>;

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  /**
   * Password is optional: when omitted (or empty string), the backend
   * creates the user in `invited` state with no password and emails them
   * a single-use link to set their own password on first login.
   */
  password?: string;
  roleIds?: string[];
}

/**
 * Response shape from `POST /users`. The `invitationSent` flag tells the UI
 * which branch the server took so it can render the right toast — the user
 * detail page when a password was supplied, or an "invitation sent" toast
 * when the new user has to go through the email-then-create-password flow.
 */
export interface CreateUserResponse extends UserDetail {
  invitationSent: boolean;
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
}

export interface InviteUserDto {
  email: string;
  roleIds?: string[];
  ttlHours?: number;
}

// ─── Billing ──────────────────────────────────────────────────────────────────

export type PlanTier = 'starter' | 'growth' | 'professional' | 'enterprise';

export interface UsageMeter {
  label: string;
  used: number;
  limit: number | null; // null = unlimited
  unit: string;
}

export interface BillingInfo {
  plan: PlanTier;
  planLabel: string;
  billingCycle: 'monthly' | 'annual';
  nextBillingDate: string;
  amount: number;
  currency: string;
  usage: UsageMeter[];
  cancelAtPeriodEnd: boolean;
}

// ─── Roles & Permissions ─────────────────────────────────────────────────────

export type PermissionScope = 'OWN' | 'TEAM' | 'ALL';

export interface RoleListItem {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  permissionCount: number;
  membershipCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RolePermission {
  resource: string;
  action: string;
  scope: PermissionScope;
}

export interface RoleDetail {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  capabilityFlags: Record<string, boolean>;
  fieldMasks: string[];
  permissions: RolePermission[];
  createdAt: string;
  updatedAt: string;
}

// ─── Groups ──────────────────────────────────────────────────────────────────

export interface GroupListItem {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  managerName: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface GroupMember {
  id: string;
  membershipId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
}

export interface GroupDetail {
  id: string;
  name: string;
  description: string | null;
  managerId: string | null;
  managerName: string | null;
  isActive: boolean;
  members: GroupMember[];
  createdAt: string;
  updatedAt: string;
}

export type RoleListResponse = PaginatedResponse<RoleListItem>;

export interface RolePermissionDto {
  key: string;
  scope: PermissionScope;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  permissions: RolePermissionDto[];
  capabilityFlags?: Record<string, boolean>;
  fieldMasks?: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  permissions?: RolePermissionDto[];
  capabilityFlags?: Record<string, boolean>;
  fieldMasks?: string[];
}

export interface CreateGroupDto {
  name: string;
  description?: string;
  managerId?: string;
  memberIds?: string[];
}

export interface UpdateGroupDto {
  name?: string;
  description?: string;
  managerId?: string | null;
  isActive?: boolean;
}

export interface AddGroupMembersDto {
  membershipIds: string[];
}

export type GroupListResponse = PaginatedResponse<GroupListItem>;

export interface AssignRolesDto {
  roleIds: string[];
}

// ─── Permission Catalog ─────────────────────────────────────────────────────

export interface CatalogPermission {
  action: string;
  key: string;
  description: string;
}

export interface CatalogResource {
  resource: string;
  permissions: CatalogPermission[];
}

/** One field-mask category, as the server describes it. */
export interface CatalogFieldMask {
  key: string;
  label: string;
  description: string;
  /** The figures this category deletes, in the operator's words. */
  hides: string[];
  /** What breaks downstream when it is on. Render as a warning, not a note. */
  consequence?: string;
}

/** One capability flag, as the server describes it. */
export interface CatalogCapabilityFlag {
  key: string;
  label: string;
  description: string;
}

export interface PermissionCatalog {
  resources: CatalogResource[];
  /**
   * The other two axes a role carries. Optional because an older API returns
   * neither, in which case the role editor falls back to its built-in copy —
   * but when present the server's copy wins, so a new mask category cannot ship
   * without a label and a role editor cannot describe a mask the interceptor
   * no longer applies.
   */
  fieldMasks?: CatalogFieldMask[];
  capabilityFlags?: CatalogCapabilityFlag[];
}

/** Human-readable labels for resource names */
export const RESOURCE_LABELS: Record<string, string> = {
  // CRM
  contacts: 'Contacts',
  leads: 'Leads',
  // Operations
  jobs: 'Jobs',
  bookings: 'Bookings',
  technicians: 'Technicians',
  calendar: 'Calendar',
  availability: 'Availability',
  reminders: 'Reminders',
  forms: 'Forms',
  // Financial
  estimates: 'Estimates',
  pricing: 'Pricing',
  line_items: 'Line Items',
  bundles: 'Bundles',
  invoices: 'Invoices',
  payments: 'Payments',
  memberships: 'Memberships',
  contracts: 'Contracts',
  // Communications
  messages: 'Messages',
  calls: 'Calls',
  message_templates: 'Message Templates',
  // Admin
  users: 'Users',
  roles: 'Roles',
  security: 'Security',
  sessions: 'Sessions',
  company: 'Company',
  billing: 'Billing',
  integrations: 'Integrations',
  numbers: 'Numbers',
  reports: 'Reports',
  activity: 'Activity',
  audit: 'Audit',
  custom_fields: 'Custom Fields',
  tags: 'Tags',
  statuses: 'Statuses',
  ai_config: 'AI Config',
  ai_agents: 'AI Agents',
  ai_decisions: 'AI Decisions',
  ai_upsell: 'AI Upsell',
};

export const RESOURCE_GROUPS: { label: string; keys: string[] }[] = [
  {
    label: 'CRM',
    keys: ['contacts', 'leads'],
  },
  {
    label: 'Operations',
    keys: ['jobs', 'bookings', 'technicians', 'calendar', 'availability', 'memberships'],
  },
  {
    label: 'Financial',
    keys: ['estimates', 'invoices', 'payments', 'pricing', 'contracts'],
  },
  {
    label: 'Communications',
    keys: ['messages', 'calls', 'message_templates'],
  },
  {
    label: 'Admin',
    keys: [
      'users',
      'roles',
      'security',
      'sessions',
      'company',
      'billing',
      'integrations',
      'numbers',
      'reports',
      'activity',
      'audit',
      'custom_fields',
      'tags',
      'statuses',
      'ai_config',
      'ai_agents',
      'ai_decisions',
      'ai_upsell',
    ],
  },
];

// ─── SMS ──────────────────────────────────────────────────────────────────────

export interface SmsNumber {
  id: string;
  number: string; // E.164
  label?: string;
  assignedUserId?: string;
  isDefault: boolean;
}

export type SmsTemplateCategory = 'booking_confirmation' | 'reminder' | 'follow_up' | 'custom';

export interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  category: SmsTemplateCategory;
  createdAt: string;
  updatedAt: string;
}

export interface SmsConfig {
  numbers: SmsNumber[];
  templates: SmsTemplate[];
  optOutMessage: string;
  doubleOptIn: boolean;
}

export interface UpdateSmsConfigDto {
  optOutMessage?: string;
  doubleOptIn?: boolean;
}

export interface UpsertSmsTemplateDto {
  name: string;
  body: string;
  category: SmsTemplateCategory;
}

// ─── Phone ────────────────────────────────────────────────────────────────────

export type PhoneNumberType = 'local' | 'toll_free';
export type PhoneNumberStatus = 'active' | 'inactive';

export interface PhoneNumber {
  id: string;
  number: string; // E.164
  label?: string;
  type: PhoneNumberType;
  status: PhoneNumberStatus;
}

export type RingStrategy = 'sequential' | 'simultaneous' | 'round_robin';

export interface CallRoutingConfig {
  primaryUserId?: string;
  fallbackUserId?: string;
  ringStrategy: RingStrategy;
  ringTimeout: number; // seconds
  voicemailEnabled: boolean;
  voicemailGreeting?: string;
}

export interface PhoneSettings {
  numbers: PhoneNumber[];
  routing: CallRoutingConfig;
}

export interface UpdatePhoneSettingsDto {
  routing?: Partial<CallRoutingConfig>;
}

// ─── Call Tree ────────────────────────────────────────────────────────────────

export type CallTreeNodeType = 'greeting' | 'ivr_menu' | 'forward' | 'voicemail' | 'schedule';

export interface IvrOption {
  key: string; // '0'-'9', '*', '#'
  label: string;
  nextNodeId: string | null;
}

export interface CallTreeNode {
  id: string;
  type: CallTreeNodeType;
  label: string;
  message?: string;
  options?: IvrOption[]; // ivr_menu only
  forwardToUserId?: string; // forward only
  scheduleWindowStart?: string; // HH:mm
  scheduleWindowEnd?: string; // HH:mm
  afterHoursNodeId?: string | null; // schedule only — where to route calls outside business hours
  nextNodeId: string | null;
  position: { x: number; y: number };
}

export interface CallTree {
  id: string;
  name: string;
  nodes: CallTreeNode[];
  entryNodeId: string | null;
}

export interface UpdateCallTreeDto {
  name?: string;
  nodes?: CallTreeNode[];
  entryNodeId?: string | null;
}

// ─── Notifications ────────────────────────────────────────────────────────────

export type NotificationChannel = 'email' | 'sms' | 'push';

export type NotificationEventType =
  | 'booking_created'
  | 'booking_updated'
  | 'booking_cancelled'
  | 'invoice_paid'
  | 'invoice_overdue'
  | 'new_message'
  | 'new_review'
  | 'lead_assigned'
  | 'job_assigned'
  | 'payment_received';

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEventType, string> = {
  booking_created: 'Booking Created',
  booking_updated: 'Booking Updated',
  booking_cancelled: 'Booking Cancelled',
  invoice_paid: 'Invoice Paid',
  invoice_overdue: 'Invoice Overdue',
  new_message: 'New Message',
  new_review: 'New Review',
  lead_assigned: 'Lead Assigned',
  job_assigned: 'Job Assigned',
  payment_received: 'Payment Received',
};

export const NOTIFICATION_EVENT_GROUPS: { label: string; events: NotificationEventType[] }[] = [
  { label: 'Bookings', events: ['booking_created', 'booking_updated', 'booking_cancelled'] },
  { label: 'Financial', events: ['invoice_paid', 'invoice_overdue', 'payment_received'] },
  { label: 'Communications', events: ['new_message', 'new_review'] },
  { label: 'CRM', events: ['lead_assigned', 'job_assigned'] },
];

export interface NotificationPreference {
  eventType: NotificationEventType;
  channels: Record<NotificationChannel, boolean>;
}

export type NotificationPreferences = NotificationPreference[];

export interface UpdateNotificationPreferencesDto {
  preferences: NotificationPreference[];
}

// ─── QuickBooks ────────────────────────────────────────────────────────────────

export type QbSyncStatus = 'idle' | 'syncing' | 'error' | 'never';

export interface QuickBooksStatus {
  connected: boolean;
  companyName?: string;
  lastSyncAt?: string;
  syncStatus: QbSyncStatus;
  syncEnabled: boolean;
}

export type SyncDirection = 'off' | 'push' | 'pull' | 'both';

export interface EntitySyncConfig {
  direction: SyncDirection;
  pushSince: string | null;
  pullSince: string | null;
}

export interface QuickBooksFieldMapping {
  invoices: EntitySyncConfig;
  contacts: EntitySyncConfig;
  payments: EntitySyncConfig;
  estimates: EntitySyncConfig;
  items: EntitySyncConfig;
  creditMemos: EntitySyncConfig;
}

export interface UpdateQuickBooksFieldMappingDto {
  invoices?: SyncDirection;
  contacts?: SyncDirection;
  payments?: SyncDirection;
  estimates?: SyncDirection;
  items?: SyncDirection;
  creditMemos?: SyncDirection;
  pushSince?: string;
  pullSince?: string;
}

// ─── Accounting Configuration (spec §16.2) ───────────────────────────────────

/** Cash basis (REVENUE) vs accrual (LIABILITY) deposit accounting. */
export type DepositMode = 'REVENUE' | 'LIABILITY';

/**
 * Notifi-internal payment method keys. The user maps these to QBO PaymentMethod IDs
 * via the Configuration tab. Keep in sync with the backend Payment.paymentMethod
 * enum.
 */
export const NOTIFI_PAYMENT_METHODS = [
  'CASH',
  'CHECK',
  'CREDIT_CARD',
  'BANK_TRANSFER',
  'ACH',
  'WIRE',
  'OTHER',
] as const;

export type NotifiPaymentMethod = (typeof NOTIFI_PAYMENT_METHODS)[number];

/**
 * GL account mapping keys. Each maps to a QBO Account ID.
 * - memberDiscountAccount / promoDiscountAccount: contra-revenue (Income with negative balance)
 * - cancellationFeeIncomeAccount: membership early-termination fee income
 * - refundBankAccount: bank account where refunds disburse from (Bank type)
 * - depositLiabilityAccount: liability for unearned revenue (mode A only)
 * - depositItem / membershipItem: lazy-created service items
 * - defaultExpenseAccount / expensePaymentAccount: expense fallback + cash account
 */
export const GL_ACCOUNT_KEYS = [
  'memberDiscountAccount',
  'promoDiscountAccount',
  'cancellationFeeIncomeAccount',
  'refundBankAccount',
  'depositLiabilityAccount',
  'depositItem',
  'membershipItem',
  'defaultExpenseAccount',
  'expensePaymentAccount',
] as const;

export type GlAccountKey = (typeof GL_ACCOUNT_KEYS)[number];

/**
 * Accounts purchasing posts to (PUR-ACC-2, -6, -8), kept in the same map:
 * accounts payable, the inventory asset, cost of goods for stock used on
 * jobs, shrinkage, purchase price variance and the company card.
 */
export const PURCHASING_ACCOUNT_KEYS = [
  'apAccount',
  'inventoryAssetAccount',
  'inventoryCogsAccount',
  'inventoryShrinkageAccount',
  'purchasePriceVarianceAccount',
  'creditCardAccount',
] as const;

export type PurchasingAccountKey = (typeof PURCHASING_ACCOUNT_KEYS)[number];

export interface QbAccount {
  id: string;
  name: string;
  type: string;
  subType?: string;
  fullyQualifiedName?: string;
}

export interface QbClass {
  id: string;
  name: string;
  fullyQualifiedName?: string;
}

export interface QbPaymentMethod {
  id: string;
  name: string;
}

export interface AccountingSettings {
  depositMode: DepositMode;
  glAccountMap: Partial<Record<GlAccountKey | PurchasingAccountKey, string>>;
  paymentMethodMap: Partial<Record<NotifiPaymentMethod, string>>;
  syncExpenses: SyncDirection;
  syncMemberships: SyncDirection;
  syncDeposits: SyncDirection;
  syncChangeOrders: SyncDirection;
  /** Per-entity push backfill window (ISO timestamp). null = "New" mode. */
  syncExpensesPushSince: string | null;
  syncMembershipsPushSince: string | null;
  syncDepositsPushSince: string | null;
  syncChangeOrdersPushSince: string | null;
  /** Pull-side window — only wired for expenses (QB Purchase). */
  syncExpensesPullSince: string | null;
  /** Purchasing: suppliers as vendors (any direction); the rest push only. */
  syncVendors: SyncDirection;
  syncBills: SyncDirection;
  syncPurchaseOrders: SyncDirection;
  syncInventoryJournal: SyncDirection;
  syncVendorsPushSince: string | null;
  syncVendorsPullSince: string | null;
  syncBillsPushSince: string | null;
  syncPurchaseOrdersPushSince: string | null;
  /** Business unit id → QuickBooks class id on purchasing lines. */
  businessUnitClassMap: Record<string, string>;
  /** Whether the QuickBooks plan has purchase orders; null until checked. */
  purchaseOrdersSupported: boolean | null;
}

export interface UpdateAccountingSettingsDto {
  depositMode?: DepositMode;
  glAccountMap?: Partial<Record<GlAccountKey | PurchasingAccountKey, string>>;
  paymentMethodMap?: Partial<Record<NotifiPaymentMethod, string>>;
  syncExpenses?: SyncDirection;
  syncMemberships?: SyncDirection;
  syncDeposits?: SyncDirection;
  syncChangeOrders?: SyncDirection;
  /** ISO timestamp; omit to let the backend default to `now()` on enable. */
  syncExpensesPushSince?: string;
  syncMembershipsPushSince?: string;
  syncDepositsPushSince?: string;
  syncChangeOrdersPushSince?: string;
  syncExpensesPullSince?: string;
  syncVendors?: SyncDirection;
  syncBills?: SyncDirection;
  syncPurchaseOrders?: SyncDirection;
  syncInventoryJournal?: SyncDirection;
  /** Merged into the stored map; an empty class removes the unit. */
  businessUnitClassMap?: Record<string, string>;
}

// ─── Tax Rates ────────────────────────────────────────────────────────────────
// Re-exported for backward compatibility with the settings tax screen.

export type { TaxRate, CreateTaxRateDto, UpdateTaxRateDto } from './taxRate';

// ─── Custom Fields (legacy — use @/types/custom-fields for new architecture) ─
// Re-exported for backward compatibility with existing entity forms.

export type {
  CustomFieldDefinition as CustomField,
  CustomFieldType,
  CustomFieldEntityType as CustomFieldEntity,
  CreateDefinitionDto as CreateCustomFieldDto,
  UpdateDefinitionDto as UpdateCustomFieldDto,
  CustomFieldConfig as CustomFieldOption,
} from './custom-fields';

// ─── Job Types ────────────────────────────────────────────────────────────────

export type JobPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'EMERGENCY';
export type PricingModel = 'FLAT_RATE' | 'HOURLY' | 'PER_UNIT';
export type RecurrenceFrequency = 'NONE' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
export type CancellationFeeType = 'NONE' | 'FLAT' | 'PERCENT';
export type CustomerRescheduleMode = 'DISABLED' | 'REQUEST' | 'SELF_SERVICE';

export interface SkillSummary {
  id: string;
  name: string;
  slug: string;
  color: string;
  /** The Trade this skill belongs to (replaces `category`). */
  trade?: { id: string; name: string; slug: string; color: string } | null;
  /** Legacy wire field. Skill eligibility is binary and this value is ignored. */
  minLevel?: number | null;
}

/** A required skill chosen in the job-type editor. Eligibility is binary. */
export interface RequiredSkillDraft {
  serviceTypeId: string;
}

/**
 * NOT-298: default work-authorization / completion sign-off gate for a job type.
 * Stored on the new job as a stable template and resolved as the default for each
 * visit. Collection and per-visit overrides live on the appointment.
 */
export interface JobTypeGateConfig {
  required: boolean;
  level?: 'JOB' | 'APPOINTMENT';
  method?: string;
  maxAmount?: number;
}

export type ArrivalWindowMinutes = 15 | 30 | 60 | 120 | 180 | 240;
export type ArrivalWindowAlignment = 'AFTER_START' | 'CENTERED';

/** §11 — default customer arrival window cascaded onto appointments of this type. */
export interface ArrivalWindowConfig {
  enabled: boolean;
  minutes: ArrivalWindowMinutes;
  alignment: ArrivalWindowAlignment;
}

export type JobTypeSchedulingAvailability = 'BOTH' | 'ONE_OFF' | 'RECURRING';
export type JobTypeOneOffVisitShape = 'TIMED' | 'ALL_DAY' | 'FULL_DAY' | 'MULTI_DAY';
export type JobTypeOneOffPatternMode = 'CHOOSE_ON_CREATE' | 'PRESET';
export type JobTypeRecurringPlacement = 'SCHEDULE_NOW' | 'ALL_DAY' | 'ROUTE_BASED';
export type JobTypeRecurringPatternMode = 'CHOOSE_ON_CREATE' | 'FIXED' | 'FLEXIBLE';
export type JobTypeRecurringDayConstraintMode = 'ANY' | 'INCLUDE' | 'EXCLUDE';

export interface JobTypeSchedulingDefaults {
  version: 1 | 2 | 3;
  availability: JobTypeSchedulingAvailability;
  oneOff: {
    patternMode?: JobTypeOneOffPatternMode;
    scheduleMode?: 'SCHEDULE_NOW' | 'SCHEDULE_LATER' | 'NO_VISIT';
    visitShape: JobTypeOneOffVisitShape;
    preferredTimeWindow: 'ANY' | 'MORNING' | 'AFTERNOON' | 'SPECIFIC';
    specificStartTime?: string | null;
    consecutiveDays?: number | null;
    urgency?: 'STANDARD' | 'URGENT' | 'EMERGENCY';
  };
  recurring: {
    /**
     * FIXED pre-fills the recurrence rule below. FLEXIBLE records a frequency
     * target without inventing weekdays. CHOOSE_ON_CREATE leaves the rule to
     * the dispatcher creating the Job.
     */
    patternMode?: JobTypeRecurringPatternMode;
    visitsPerCycle?: number | null;
    dayConstraintMode?: JobTypeRecurringDayConstraintMode;
    allowedWeekDays?: number[];
    placement: JobTypeRecurringPlacement;
    frequency: RecurrenceFrequency;
    interval: number;
    weekDays?: number[];
    monthMode?: 'DAY_OF_MONTH' | 'DAY_OF_WEEK';
    monthDay?: number | null;
    monthLastDay?: boolean;
    monthDayOfWeek?: number | null;
    monthDayOfWeekOccurrence?: number | null;
    annualMonth?: number | null;
    annualDay?: number | null;
    timeMode?: 'SAME' | 'PER_VISIT';
    technicianMode?: 'SAME' | 'PER_VISIT';
    visitStartTime?: string | null;
    visitEndTime?: string | null;
  };
}

/** New writes are exclusive; BOTH remains only on the read model for legacy rows. */
export type JobTypeWriteSchedulingDefaults = Omit<JobTypeSchedulingDefaults, 'availability'> & {
  availability: 'ONE_OFF' | 'RECURRING';
};

export type JobTypeIrisMode = 'LISTED' | 'NOT_LISTED' | 'SERVICE_CALL';

export interface JobTypeIrisProfile {
  version: 1;
  customerDescription?: string;
  matchingExclusions?: string[];
  includedWork?: string[];
  excludedWork?: string[];
  faqs?: Array<{ question: string; answer: string }>;
  warranty?: string;
  additionalGuidance?: string;
}

/** One Iris intake-survey question; `id` is stable so answers stay keyed. */
export interface JobTypeIrisSurveyQuestion {
  id: string;
  question: string;
  type?: 'TEXT' | 'PHOTO';
}

export interface JobTypeBusinessUnitSummary {
  id: string;
  name: string;
  code: string;
  segment: 'RESIDENTIAL' | 'COMMERCIAL';
  color: string;
  status: 'ACTIVE' | 'ARCHIVED';
}

export interface JobType {
  id: string;
  /** Number of jobs currently using this job type. */
  jobCount: number;
  name: string;
  shortCode: string;
  color: string;
  icon: string | null;
  description: string | null;
  /** Natural customer wording Iris should map to this job type. */
  customerPhrases: string[];
  /** Diagnostic fallback for broad requests matching this type's skills. */
  isServiceCallFallback: boolean;
  /** Whether the visit discovers the final work scope on site. */
  scopeMode?: 'DEFINED' | 'DIAGNOSTIC';
  /** @deprecated Rolling-deploy compatibility alias for scopeMode. */
  serviceCallEnabled?: boolean;
  /**
   * Per-type visit-price rule, configured by the drawer's pricing radio:
   * CREDIT_ON_ESTIMATE, WAIVE_ON_CALL (default), or NONE (always charged).
   */
  serviceCallFeePolicy?: string | null;
  /**
   * Visit deposit — an upfront deposit is required to book a visit of this
   * type. Iris/online booking send a payment link at booking time. FLAT
   * dollars or PERCENT of the visit price; Decimal serializes as a string.
   */
  visitDepositEnabled?: boolean;
  visitDepositType?: 'FLAT' | 'PERCENT';
  visitDepositValue?: number | string;
  irisMode: JobTypeIrisMode;
  irisProfile?: JobTypeIrisProfile | null;
  /** Iris intake survey — simple questions saved onto the created job (≤10). */
  irisSurveyQuestions?: JobTypeIrisSurveyQuestion[];
  /** When Iris asks the intake questions (BEFORE_SLOTS default | AFTER_SLOT). */
  irisSurveyTiming?: string | null;
  /** Iris asks whether the visit concerns equipment the customer has on file. */
  irisAskEquipment?: boolean;
  onDutySchedulingEnabled: boolean;
  /** Visits wait on the job's parts (PUR-INV-31). */
  requiresParts?: boolean;
  defaultDuration: number;
  defaultPriority: JobPriority;
  recurrenceFrequency: RecurrenceFrequency;
  recurrenceInterval: number;
  minCrewSize: number;
  defaultPrice: number | null;
  /**
   * Pricebook line-item snapshots that define this job type's price. A job
   * created from this type pre-fills them. Same loose shape as a job's line
   * items (see `Job.lineItems`).
   */
  lineItems?: Array<Record<string, unknown>>;
  pricingModel: PricingModel;
  billingCategory: string | null;
  requiredSkills: SkillSummary[];
  isActive: boolean;
  isDefault?: boolean;
  pipelineId?: string | null;
  // Business Units this job type can roll up to, in preference order (empty =
  // unassigned). The unit a job is stamped with is resolved per record from
  // the customer segment / service territory / technician.
  businessUnitIds: string[];
  // Server-hydrated summary of `businessUnitIds` (same order).
  businessUnits?: JobTypeBusinessUnitSummary[];
  authorizationConfig?: JobTypeGateConfig | null;
  signoffConfig?: JobTypeGateConfig | null;
  arrivalWindowConfig?: ArrivalWindowConfig | null;
  schedulingDefaults?: JobTypeSchedulingDefaults | null;
  // Cancellation policy (SPEC-sched-cancel-fee-policy / -deposit-refund).
  cancellationWindowHours?: number | null;
  cancellationFeeType?: CancellationFeeType;
  cancellationFeeAmount?: number | null;
  depositRefundable?: boolean;
  customerRescheduleMode?: CustomerRescheduleMode;
  rescheduleCutoffHours?: number;
  maxCustomerReschedules?: number;
  // NOT-362 — inheritable workflow defaults (create-form Advanced).
  defaultStageId?: string | null;
  schedulingNotificationPolicy?: 'ASK' | 'NONE' | 'SMS' | 'EMAIL' | 'BOTH';
  confirmationMethod?: JobTypeConfirmationMethod | null;
  invoicingMethod?: JobTypeInvoicingMethod | null;
  invoicingSchedule?: JobTypeInvoicingSchedule | null;
  autoInvoice?: boolean | null;
  autoCharge?: boolean | null;
  reviewRequestDefault?: boolean | null;
  weatherPolicy?: JobTypeWeatherPolicy | null;
  // Terms & Conditions default cascaded onto every job of this type. A
  // *selection* only — the text is resolved when the job is created, so
  // promoting a new tenant default flows through immediately.
  termsMode?: string | null;
  termsTemplateIds?: string[] | null;
  termsAndConditions?: string | null;
  // Job tags (scope 'job') stamped onto every job created with this type —
  // unioned with whatever tags the create itself supplies.
  tagIds?: string[];
  createdAt: string;
  updatedAt: string;
}

/** NOT-362 — inheritable workflow-default value types on a Job Type. */
export type JobTypeConfirmationMethod = 'SMS' | 'EMAIL' | 'BOTH';
export type JobTypeInvoicingMethod = 'PER_VISIT' | 'FIXED_PRICE';
export type JobTypeInvoicingSchedule =
  | 'ON_COMPLETION'
  | 'LAST_DAY_OF_MONTH'
  | 'CUSTOM_SCHEDULE'
  | 'AS_NEEDED';
export type JobTypeWeatherPolicy = 'SKIP' | 'MAKE_UP';

export interface CreateJobTypeDto {
  name: string;
  shortCode?: string;
  color?: string;
  icon?: string;
  isDefault?: boolean;
  pipelineId?: string | null;
  // Business Unit assignments, in preference order; [] unassigns.
  businessUnitIds?: string[];
  // Job tags stamped onto every job created with this type; [] clears.
  tagIds?: string[];
  description?: string;
  customerPhrases?: string[];
  isServiceCallFallback?: boolean;
  scopeMode?: 'DEFINED' | 'DIAGNOSTIC';
  /** @deprecated Rolling-deploy compatibility alias for scopeMode. */
  serviceCallEnabled?: boolean;
  /**
   * Visit-price rule for service calls (the drawer's pricing radio). Omitted
   * while the service-call capability is off so the stored value is left
   * untouched.
   */
  serviceCallFeePolicy?: 'CREDIT_ON_ESTIMATE' | 'WAIVE_ON_CALL' | 'NONE';
  /** Visit deposit — omitted while the service-call capability is off. */
  visitDepositEnabled?: boolean;
  visitDepositType?: 'FLAT' | 'PERCENT';
  visitDepositValue?: number;
  irisMode?: JobTypeIrisMode;
  irisProfile?: JobTypeIrisProfile;
  // Iris intake survey (≤10 questions with stable ids) + equipment awareness.
  irisSurveyQuestions?: JobTypeIrisSurveyQuestion[];
  irisSurveyTiming?: 'BEFORE_SLOTS' | 'AFTER_SLOT';
  irisAskEquipment?: boolean;
  onDutySchedulingEnabled?: boolean;
  requiresParts?: boolean;
  defaultDuration?: number;
  defaultPriority?: JobPriority;
  recurrenceFrequency?: RecurrenceFrequency;
  recurrenceInterval?: number;
  minCrewSize?: number;
  /**
   * Pricebook line-item snapshots that define this job type's price. Same loose
   * shape as a job's line items; supersedes the free-form defaultPrice.
   */
  lineItems?: Array<Record<string, unknown>>;
  requiredSkillIds?: string[];
  /** Structured required skills. When present it is authoritative over requiredSkillIds. */
  requiredSkills?: RequiredSkillDraft[];
  isActive?: boolean;
  authorizationConfig?: JobTypeGateConfig;
  signoffConfig?: JobTypeGateConfig;
  arrivalWindowConfig?: ArrivalWindowConfig;
  schedulingDefaults?: JobTypeWriteSchedulingDefaults;
  cancellationWindowHours?: number | null;
  cancellationFeeType?: CancellationFeeType;
  cancellationFeeAmount?: number | null;
  depositRefundable?: boolean;
  customerRescheduleMode?: CustomerRescheduleMode;
  rescheduleCutoffHours?: number;
  maxCustomerReschedules?: number;
  // NOT-362 — inheritable workflow defaults.
  defaultStageId?: string | null;
  schedulingNotificationPolicy?: 'ASK' | 'NONE' | 'SMS' | 'EMAIL' | 'BOTH';
  confirmationMethod?: JobTypeConfirmationMethod | null;
  invoicingMethod?: JobTypeInvoicingMethod | null;
  invoicingSchedule?: JobTypeInvoicingSchedule | null;
  autoInvoice?: boolean | null;
  autoCharge?: boolean | null;
  reviewRequestDefault?: boolean | null;
  weatherPolicy?: JobTypeWeatherPolicy | null;
  termsMode?: string | null;
  termsTemplateIds?: string[] | null;
  termsAndConditions?: string;
}

export type UpdateJobTypeDto = Partial<CreateJobTypeDto>;

// ─── Checklists ──────────────────────────────────────────────────────────────

export type ChecklistItemType =
  | 'CHECKBOX'
  | 'YES_NO'
  | 'PASS_FAIL'
  | 'TEXT'
  | 'NUMBER'
  | 'PHOTO'
  | 'SIGNATURE'
  | 'DROPDOWN'
  | 'DATE'
  | 'BARCODE_SCAN'
  | 'MEASUREMENT';

export type ChecklistVisibility = 'INTERNAL' | 'CUSTOMER_VISIBLE';
export type ChecklistTemplateStatus = 'ACTIVE' | 'ARCHIVED';
export type ChecklistInstanceStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface ChecklistItemConfig {
  // NUMBER / MEASUREMENT
  min?: number;
  max?: number;
  unit?: string;
  units?: string[];
  // DROPDOWN
  options?: string[];
  // SIGNATURE
  signerRole?: string;
  // BARCODE_SCAN
  format?: 'UPC' | 'QR' | 'CODE128' | 'ANY';
  // DATE
  allowPast?: boolean;
  allowFuture?: boolean;
  // PHOTO (standalone)
  minPhotos?: number;
  maxPhotos?: number;
  // TEXT
  maxLength?: number;
}

export interface ChecklistTemplateItem {
  id?: string;
  placementKey?: string;
  questionId?: string;
  questionVersionId?: string;
  label: string;
  type: ChecklistItemType;
  required: boolean;
  description?: string | null;
  photoRequired?: boolean;
  minPhotos?: number;
  config?: ChecklistItemConfig;
}

export interface ChecklistTemplateSection {
  id?: string;
  name: string;
  order: number;
  isRepeatable?: boolean;
  repeatOptions?: string[];
  items: ChecklistTemplateItem[];
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description?: string | null;
  version?: number;
  status?: ChecklistTemplateStatus;
  defaultVisibility?: ChecklistVisibility;
  jobTypeLinks?: ChecklistJobTypeLink[];
  sections: ChecklistTemplateSection[];
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistDefinitionVersion {
  id: string;
  tenantId?: string;
  definitionId: string;
  version: number;
  name: string;
  description?: string | null;
  sections: ChecklistTemplateSection[];
  createdAt: string;
  createdBy?: string | null;
}

export interface ChecklistJobTypeLink {
  id?: string;
  jobTypeId: string;
  checklistDefinitionId?: string;
  blocksCompletion?: boolean;
  sortOrder?: number;
}

export interface CreateChecklistTemplateDto {
  name: string;
  description?: string;
  defaultVisibility?: ChecklistVisibility;
  jobTypeLinks?: {
    jobTypeId: string;
    blocksCompletion?: boolean;
    sortOrder?: number;
  }[];
  sections: {
    id?: string;
    name: string;
    order: number;
    isRepeatable?: boolean;
    repeatOptions?: string[];
    items: {
      id?: string;
      placementKey?: string;
      questionId?: string;
      questionVersionId?: string;
      label: string;
      type: ChecklistItemType;
      required?: boolean;
      description?: string;
      photoRequired?: boolean;
      minPhotos?: number;
      config?: ChecklistItemConfig;
    }[];
  }[];
}

export type UpdateChecklistTemplateDto = Partial<CreateChecklistTemplateDto>;

// ─── Checklist Instances (appointment-scoped) ───────────────────────────────

export interface ChecklistItemPhoto {
  id: string;
  attachmentId?: string;
  url?: string | null;
  thumbnailUrl?: string | null;
  uploadedBy?: string | null;
  uploadedAt?: string | null;
  caption?: string | null;
}

export type ChecklistItemValue =
  | boolean
  | string
  | string[]
  | number
  | { value: number; unit: string }
  | { data: string; signerName?: string }
  | null;

export interface ChecklistInstanceItem {
  itemDefId: string;
  label: string;
  type: ChecklistItemType;
  required: boolean;
  description?: string | null;
  photoRequired?: boolean;
  minPhotos?: number;
  config?: ChecklistItemConfig | null;
  value: ChecklistItemValue;
  photos: ChecklistItemPhoto[];
  completedBy?: string | null;
  completedAt?: string | null;
}

export interface ChecklistInstanceSection {
  sectionDefId: string;
  name: string;
  sortOrder: number;
  repeatLabel?: string | null;
  repeatIndex?: number | null;
  items: ChecklistInstanceItem[];
}

export interface ChecklistInstance {
  id: string;
  appointmentId: string;
  jobId: string | null;
  templateId: string;
  templateName: string;
  definitionVersion: number;
  blocksCompletion?: boolean;
  status: ChecklistInstanceStatus;
  visibility: ChecklistVisibility;
  sections: ChecklistInstanceSection[];
  startedAt?: string | null;
  completedAt?: string | null;
  completedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistReportData {
  instanceId: string;
  templateName: string;
  appointmentId: string;
  appointmentDate?: string | null;
  jobId: string | null;
  jobNumber?: string | null;
  jobName?: string | null;
  customer?: {
    id?: string | null;
    name?: string | null;
    companyName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  serviceAddress?: string | null;
  technician?: {
    id?: string | null;
    name?: string | null;
    email?: string | null;
  } | null;
  completedAt?: string | null;
  completedBy?: string | null;
  sections: ChecklistInstanceSection[];
}

export interface ChecklistReportResponse {
  pdf: { id?: string; url?: string; createdAt?: string } | null;
  downloadUrl?: string;
  reportData?: ChecklistReportData;
}

export interface ChecklistReportUploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
}

export interface ChecklistSnapshot {
  appointmentId?: string;
  downloadedAt?: string;
  instances: ChecklistInstance[];
  definitionVersions?: unknown[];
  templateSnapshots?: Record<string, unknown>;
}

export interface ChecklistOfflineUpdate {
  instanceId: string;
  sectionIdx: number;
  itemIdx: number;
  value: ChecklistItemValue;
  timestamp: string;
}

export interface UpdateChecklistItemValueDto {
  value: ChecklistItemValue;
}

export interface SetRepeatSelectionsDto {
  sectionDefId: string;
  selections: string[];
}

export interface UpdateChecklistVisibilityDto {
  visibility: ChecklistVisibility;
}

// ─── Expenses ────────────────────────────────────────────────────────────────

export type ExpenseType =
  | 'MATERIALS'
  | 'EQUIPMENT_RENTAL'
  | 'SUBCONTRACTOR'
  | 'TRAVEL'
  | 'PERMIT'
  | 'DISPOSAL'
  | 'OTHER';

export type ExpenseStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'VOIDED' | 'REIMBURSED';
export type ReimbursementStatus = 'NOT_REIMBURSABLE' | 'PENDING' | 'APPROVED' | 'PAID';

export interface Expense {
  supplierId?: string | null;
  id: string;
  jobId: string;
  appointmentId: string | null;
  expenseType: ExpenseType;
  name: string;
  description: string | null;
  quantity: number;
  unitCost: number;
  total: number;
  attachments?: Attachment[];
  vendor: string | null;
  date: string;
  status: ExpenseStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  reimburseToUserId: string | null;
  reimbursementStatus: ReimbursementStatus;
  reimbursementPaidAt: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseDto {
  supplierId?: string;
  expenseType: ExpenseType;
  name: string;
  description?: string;
  quantity: number;
  unitCost: number;
  vendor?: string;
  date: string;
  appointmentId: string;
  reimburseToUserId?: string;
  status?: ExpenseStatus;
  pendingId?: string;
}

export interface UpdateExpenseDto {
  supplierId?: string | null;
  expenseType?: ExpenseType;
  name?: string;
  description?: string | null;
  quantity?: number;
  unitCost?: number;
  vendor?: string | null;
  date?: string;
  appointmentId?: string;
  reimburseToUserId?: string | null;
  reimbursementStatus?: ReimbursementStatus;
}

export type ExpenseListResponse = PaginatedResponse<Expense>;

/** Job cost ledger categories (operations `JobCostCategory`). */
export type JobCostCategory =
  | 'MATERIAL'
  | 'EQUIPMENT'
  | 'SUBCONTRACTOR'
  | 'EQUIPMENT_RENTAL'
  | 'TRAVEL'
  | 'PERMIT'
  | 'DISPOSAL'
  | 'FREIGHT'
  | 'OTHER';

export type JobCostByCategory = Record<JobCostCategory, number>;

export interface JobProfitability {
  invoiced: number;
  /** Approved expenses, as the expense list shows them. */
  expenses: number;
  /** Recorded job cost: the ledger total, each dollar counted once. */
  costs?: number;
  costsByCategory?: JobCostByCategory;
  /** Open purchase-order value still to be received for the job. */
  committed?: number;
  committedByCategory?: JobCostByCategory;
  profit: number;
}

// ─── Chatbot Config ───────────────────────────────────────────────────────────

export interface ChatbotConfig {
  enabled: boolean;
  greeting?: string;
  businessHoursOnly: boolean;
  collectName: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  fallbackMessage?: string;
  model?: string;
}

export type UpdateChatbotConfigDto = Partial<ChatbotConfig>;

// ─── Wisetack Settings ───────────────────────────────────────────────────────

export type WisetackEnvironment = 'mock' | 'sandbox' | 'production';

export type WisetackEnrollmentState =
  | 'not_configured'
  | 'not_enrolled'
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'declined'
  | 'expired';

export interface WisetackSettings {
  enabled: boolean;
  environment: WisetackEnvironment;
  partnerName: string | null;
  signupUrl: string | null;
  prequalLink: string | null;
  enrollmentState: WisetackEnrollmentState;
  merchantId: string | null;
  merchantExternalId: string | null;
  transactionRange: string | null;
  pricingPlan: string | null;
  requestedDocuments: string | null;
  merchantBusinessName: string | null;
  merchantDoingBusinessAs: string | null;
  transactionsEnabled: boolean;
  /**
   * Staging aid, provisioned per tenant. While it is on, the Wisetack panel
   * offers a dropdown that sets the enrollment state by hand instead of waiting
   * on a merchant report. Off on every real tenant.
   */
  statusSimulatorEnabled: boolean;
  lastMerchantReportAt: string | null;
  statusDates: {
    inProgressAt: string | null;
    submittedAt: string | null;
    approvedAt: string | null;
    declinedAt: string | null;
    expiredAt: string | null;
  };
}

export type UpdateWisetackSettingsDto = Partial<WisetackSettings>;

// ─── Payment Settings ─────────────────────────────────────────────────────────

export type StripeConnectStatus = 'not_connected' | 'pending' | 'connected' | 'restricted';

/**
 * Categories the FE uses to group Stripe requirements visually on the
 * integrations page. Mirrors the BE mapper output — see
 * apps/operations/src/modules/sales/payments/settings/stripe-requirements.mapper.ts.
 */
export type StripeRequirementCategory = 'individual' | 'business' | 'tos' | 'bank' | 'other';

export interface StripeRequirementSummary {
  /** Raw Stripe code, e.g. `person_xxx.id_number`. Used as a stable React key. */
  code: string;
  category: StripeRequirementCategory;
  label: string;
  description?: string;
}

export interface StripeRequirementsInfo {
  /** Stripe's machine `requirements.disabled_reason`, e.g. `"requirements.past_due"`. */
  disabledReason: string | null;
  currentlyDue: StripeRequirementSummary[];
  pastDue: StripeRequirementSummary[];
  pendingVerification: StripeRequirementSummary[];
  /** ISO timestamp; null when Stripe hasn't set a hard deadline. */
  currentDeadline: string | null;
}

export interface StripeConnectInfo {
  status: StripeConnectStatus;
  accountId?: string;
  displayName?: string;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
  detailsSubmitted?: boolean;
  /**
   * Present when the account exists and the BE successfully fetched its
   * requirements payload from Stripe. `null` indicates the BE could not
   * reach Stripe (timeout / API error) — render the simpler connected
   * indicator and skip the proactive requirements card.
   */
  requirements?: StripeRequirementsInfo | null;
}

export interface PaymentSettings {
  stripe: StripeConnectInfo;
  primaryProcessor: ProcessorEnum;
  acceptCreditCards: boolean;
  acceptAch: boolean;
  acceptWallet: boolean;
  allowCustomerSaveOnPayPage: boolean;
  ccSurchargeEnabled: boolean;
  serviceFeePercent?: number;
  surchargePercent: number;
  achVerificationRequired: boolean;
  autoChargeDefault: AutoChargeDefaultEnum;
}

export interface UpdatePaymentSettingsDto {
  acceptCreditCards?: boolean;
  acceptAch?: boolean;
  serviceFeePercent?: number;
}

export interface StripeConnectLinkResponse {
  url: string;
}

export interface StripeAccountSessionResponse {
  clientSecret: string;
  publishableKey: string;
  accountId: string;
}

// ─── Billing Settings (PR 9 — spec §10) ──────────────────────────────────────

export type InvoiceTermsEnum = 'on_receipt' | 'net_7' | 'net_14' | 'net_30' | 'custom';

export interface InvoiceBranding {
  logoUrl?: string;
  primaryColor?: string;
}

export interface InvoiceReminderSettings {
  preDueDays: number;
  onDueAtTimeOfDay: string;
  overdueOffsets: number[];
}

export interface InvoicesTabSettings {
  numberPrefix: string;
  sequenceStart: number;
  defaultTerms: InvoiceTermsEnum;
  allowCustomTerms: boolean;
  requireApprovalThreshold: number;
  autoSendOnJobCompletion: boolean;
  autoSendRecurringInvoices: boolean;
  attachJobNotes: boolean;
  footerText: string;
  branding: InvoiceBranding;
  reminders: InvoiceReminderSettings;
}

export interface ReceiptsTabSettings {
  numberPrefix: string;
  autoEmail: boolean;
  autoSms: boolean;
  includeSurchargeBreakdown: boolean;
  footerText: string;
}

export type ForfeiturePolicyEnum = 'refund_full' | 'forfeit_full' | 'percent_forfeit';

export interface DepositsTabSettings {
  defaultPercent: number;
  requireBeforeScheduling: boolean;
  forfeiturePolicy: ForfeiturePolicyEnum;
  forfeiturePercent?: number;
  forfeitureRevenueAccount?: string;
  deferredRevenueAccount?: string;
}

export type ProcessorEnum = 'stripe' | 'payabli';
export type AutoChargeDefaultEnum = 'opt_in' | 'opt_out';

/**
 * Browser-safe config for the Payabli embedded save-a-card component
 * (GET /api/settings/payments/payabli-client-config). `publicToken` is null
 * until the platform token is provisioned, so the UI renders a "not
 * configured" state instead of a broken component.
 */
export interface PayabliClientConfig {
  connected: boolean;
  entryPoint: string | null;
  publicToken: string | null;
  mode: 'TEST' | 'LIVE';
  /**
   * Rails the tenant accepts. REQUIRED — the embedded component enables both
   * unless told otherwise, so an absent value must fail closed, never default
   * to enabled.
   */
  acceptCard: boolean;
  acceptACH: boolean;
}

/**
 * Tenant Payabli connection status (GET
 * /api/settings/payments/payabli-connect). `entryPointMasked` is a masked
 * identifier only; the status endpoint is readable with `company:view`, so
 * the backend never exposes the full paypoint value here.
 */
export interface PayabliConnection {
  connected: boolean;
  entryPointMasked: string | null;
  mode: 'TEST' | 'LIVE';
  /**
   * Whether Payabli is actually configured to send notifications back to us.
   * Distinct from `connected`: credentials can be valid while the paypoint
   * delivers nothing, which leaves every payment unreconciled behind a green
   * "connected" badge.
   */
  webhooksRegistered: boolean;
  /** Why registration is incomplete, so the UI can warn with a real reason. */
  webhookRegistrationError: string | null;
}

export interface PaymentMethodsTabSettings {
  availableProcessors: { stripe: boolean; payabli: boolean };
  primaryProcessor: ProcessorEnum;
  acceptCard: boolean;
  acceptACH: boolean;
  /**
   * Staff may scan a customer's paper check (Payabli remote deposit capture).
   * Payabli only; also needs Payabli to enable it on the paypoint.
   */
  acceptChequeScan?: boolean;
  acceptWallet: boolean;
  allowCustomerSaveOnPayPage: boolean;
  /** A true discount from the final balance, only for ACH and check. */
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  ccSurchargeEnabled: boolean;
  surchargePercent: number;
  achVerificationRequired: boolean;
  autoChargeDefault: AutoChargeDefaultEnum;
  /** Offer the customer a gratuity. Default on. */
  tipsEnabled: boolean;
  /** Preset gratuity percentages. Empty = no presets, custom amount only. */
  tipPercents: number[];
}

export interface CreditMemosTabSettings {
  badDebtAccount?: string;
  goodwillAccount?: string;
  writeOffApprovalThreshold: number;
  allowCashRefundOfCredit: boolean;
  defaultReasonCodes: string[];
}

export type UndepositedFundsHandlingEnum = 'use_undeposited' | 'direct_to_bank';
export type SyncCadenceEnum = 'event_driven' | 'hourly_cron';

export interface QboConnectedCompany {
  realmId: string;
  companyName: string;
}

export interface QboTabSettings {
  syncEnabled: boolean;
  /** Read-only — sourced from QbConnection. */
  connectedCompany?: QboConnectedCompany;
  defaultRevenueAccount?: string;
  defaultArAccount?: string;
  deferredRevenueAccount?: string;
  undepositedFundsHandling: UndepositedFundsHandlingEnum;
  syncCadence: SyncCadenceEnum;
  paused: boolean;
  lastReconciliationReportId?: string;
}

export interface BillingSettings {
  invoices: InvoicesTabSettings;
  receipts: ReceiptsTabSettings;
  deposits: DepositsTabSettings;
  paymentMethods: PaymentMethodsTabSettings;
  creditMemos: CreditMemosTabSettings;
  qbo: QboTabSettings;
}

export type BillingSettingsTab = keyof BillingSettings;

/** A type-safe map of tab → partial DTO shape for PATCH requests. */
export type BillingTabPatchMap = {
  invoices: Partial<Omit<InvoicesTabSettings, 'branding' | 'reminders'>> & {
    branding?: Partial<InvoiceBranding>;
    reminders?: Partial<InvoiceReminderSettings>;
  };
  receipts: Partial<ReceiptsTabSettings>;
  deposits: Partial<DepositsTabSettings>;
  paymentMethods: Partial<PaymentMethodsTabSettings>;
  creditMemos: Partial<CreditMemosTabSettings>;
  qbo: Partial<Omit<QboTabSettings, 'connectedCompany'>>;
};
